import { Sentry } from "@sentryClient";
import { showToast } from "./toast";

export const UNEXPECTED_RESPONSE_MESSAGE = "Something's off on our end.";
export const UNEXPECTED_SERVER_RESPONSE_MESSAGE =
  "We hit an unexpected server response. Please retry.";

type SentryScope = {
  setTag: (key: string, value: string) => void;
  setExtra: (key: string, value: unknown) => void;
};

type ReportUnexpectedResponseOptions = {
  requestId?: string | null;
  userMessage?: string;
};

export const extractRequestId = (headers: unknown): string | null => {
  if (!headers || typeof headers !== "object") return null;
  const record = headers as Record<string, unknown>;
  const candidates = ["x-request-id", "rndr-id", "cf-ray"];

  for (const key of candidates) {
    for (const [headerKey, value] of Object.entries(record)) {
      if (headerKey.toLowerCase() !== key) continue;
      if (typeof value === "string" && value.trim() !== "") return value;
      if (typeof value === "number") return String(value);
    }
  }

  return null;
};

export const buildUnexpectedServerResponseMessage = (
  requestId?: string | null,
): string =>
  requestId && requestId.trim() !== ""
    ? `${UNEXPECTED_SERVER_RESPONSE_MESSAGE} Request id: ${requestId}`
    : UNEXPECTED_SERVER_RESPONSE_MESSAGE;

export class UnexpectedResponseError extends Error {
  requestId: string | null;
  userMessage: string;

  constructor(
    context: string,
    options: { requestId?: string | null; userMessage?: string } = {},
  ) {
    super(`Unexpected response shape for ${context}`);
    this.name = "UnexpectedResponseError";
    this.requestId = options.requestId ?? null;
    this.userMessage = options.userMessage ?? UNEXPECTED_RESPONSE_MESSAGE;
  }
}

export const reportUnexpectedResponse = (
  context: string,
  payload: unknown,
  options: ReportUnexpectedResponseOptions = {},
): UnexpectedResponseError => {
  const error = new UnexpectedResponseError(context, {
    requestId: options.requestId,
    userMessage: options.userMessage,
  });

  try {
    if (typeof Sentry.withScope === "function") {
      Sentry.withScope((scope) => {
        const typedScope = scope as SentryScope;
        typedScope.setTag("response_context", context);
        if (error.requestId) typedScope.setTag("request_id", error.requestId);
        try {
          typedScope.setExtra("payload", payload);
        } catch {
          typedScope.setExtra("payload", "[unserializable]");
        }
        Sentry.captureException(error);
      });
    }
  } catch (captureError) {
    console.error("Failed to report unexpected response", captureError);
  }

  showToast(error.userMessage, "error");
  return error;
};
